/**
 * Max Quantity Validator
 * 
 * This script validates that the quantity being added to cart doesn't exceed
 * the maximum allowed quantity defined in the product's custom.max_cart_quantity metafield.
 */

class MaxQuantityValidator {
  constructor() {
    this.init();
    this.formSubmitting = false;
    this.lastCartUpdate = Date.now();
  }

  init() {
    // Listen for form submissions with capture phase to ensure we catch it early
    document.addEventListener('submit', this.handleFormSubmit.bind(this), true);

    // Listen for cart changes (items removed from cart drawer)
    document.addEventListener('theme:cart:updated', this.handleCartUpdate.bind(this));
    document.addEventListener('theme:cart:refresh', this.handleCartUpdate.bind(this));
  }

  handleCartUpdate() {
    // Update the timestamp when the cart was last updated
    this.lastCartUpdate = Date.now();
  }

  async handleFormSubmit(event) {
    // Prevent double processing
    if (this.formSubmitting) return;

    // Only handle product forms
    if (!event.target.matches('form[action*="/cart/add"]')) return;

    const form = event.target;

    // Always invalidate any cached cart data
    this.lastCartUpdate = Date.now();
    const variantIdInput = form.querySelector('[name="id"]');
    if (!variantIdInput) {
      return;
    }
    const variantId = variantIdInput.value;
    
    let maxCartQuantityInput = form.querySelector('[data-max-cart-quantity]');
    if (!maxCartQuantityInput) return;
    const maxCartQuantity = parseInt(maxCartQuantityInput.getAttribute('data-max-cart-quantity'), 10);

    // If no max quantity set, proceed normally
    if (!maxCartQuantity || isNaN(maxCartQuantity)) return;

    // Prevent default immediately to ensure we control the submission
    event.preventDefault();
    event.stopPropagation();

    this.formSubmitting = true;

    // Disable the submit button while we validate and potentially add to cart
    const pendingSubmitButton = form.querySelector('[type="submit"]');
    if (pendingSubmitButton) {
      pendingSubmitButton.setAttribute('disabled', 'disabled');
      pendingSubmitButton.setAttribute('aria-busy', 'true');
    }

    try {
      // Get current quantity in cart      
      const currentQuantityInCart = await this.getQuantityInCart(variantId);
      const quantityToAdd = this.getQuantityFromForm(form);
      const totalQuantity = currentQuantityInCart + quantityToAdd;

      // Check if adding this quantity would exceed the max
      if (totalQuantity > maxCartQuantity) {
        // Calculate how many more can be added
        const remainingQuantity = Math.max(0, maxCartQuantity - currentQuantityInCart);

        // Show error message
        this.showErrorMessage(form, maxCartQuantity, currentQuantityInCart, remainingQuantity);
        this.formSubmitting = false;
        // Re-enable the button since we blocked submission
        if (pendingSubmitButton) {
          pendingSubmitButton.removeAttribute('aria-busy');
          pendingSubmitButton.removeAttribute('disabled');
        }
      } else {
        // If we're under the limit, manually submit the form
        this.submitForm(form);
      }
    } catch (error) {
      // If there's an error, allow the form to submit normally
      this.formSubmitting = false;
      this.submitForm(form);
    }
  }

  async getQuantityInCart(variantId) {
    try {
      // Force a fresh cart read to avoid CDN/browser caching
      const ts = Date.now();
      const response = await fetch(`/cart.js?_=${ts}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      const cart = await response.json();

      // Find the item in the cart and return its quantity
      const item = cart.items.find(item => {
        const itemVariantId = item.variant_id.toString();
        const searchVariantId = variantId.toString();
        return itemVariantId === searchVariantId;
      });

      const quantity = item ? item.quantity : 0;
      return quantity;
    } catch (error) {
      return 0;
    }
  }

  submitForm(form) {
    // Get the form data
    const formData = new FormData(form);

    // Ensure the quantity used for AJAX submission matches the validated one
    const ensuredQty = this.getQuantityFromForm(form);
    formData.set('quantity', ensuredQty.toString());

    // Set the button to loading state
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.setAttribute('disabled', 'disabled');
    }

    // Prepare sections to bundle (needed for cart drawer updates)
    const sectionsToBundle = ['variant-added'];
    formData.set('sections', sectionsToBundle.join(','));
    formData.set('sections_url', `${window.Shopify?.routes?.root || '/'}variants/${form.querySelector('[name="id"]').value}`);

    // Add the product to the cart using AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => response.json())
    .then(async (responseJson) => {
      // Reset loading state
      if (submitButton) {
        submitButton.removeAttribute('aria-busy');
        submitButton.removeAttribute('disabled');
      }
      this.formSubmitting = false;

      // Get updated cart content + force a fresh cart read to avoid CDN/browser caching
      const ts = Date.now();
      const cartResponse = await fetch(`/cart.js?_=${ts}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      const cartContent = await cartResponse.json();
      cartContent.sections = responseJson.sections;

      // Trigger variant:add event (this is what the theme listens for)
      document.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
          cart: cartContent
        }
      }));

      // Trigger cart:change event (this updates the cart drawer and bubble)
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent
        }
      }));

      // Force a complete refresh of the cart drawer
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    })
    .catch(error => {
      // Reset loading state
      if (submitButton) {
        submitButton.removeAttribute('aria-busy');
        submitButton.removeAttribute('disabled');
      }
      this.formSubmitting = false;

      // If there's an error, fall back to regular form submission
      form.submit();
    });
  }

  getQuantityFromForm(form) {
    const formId = form.getAttribute('id');
    if (formId) {
      const associated = document.querySelector(`input[name="quantity"][form="${formId}"]`);
      if (associated) {
        const val = parseInt(associated.value, 10);
        return val;
      }
    }

    return 1;
  }

  showErrorMessage(form, maxQuantity, currentQuantity, remainingQuantity) {
    // Get messages from theme settings or fall back to translation strings
    let message = '';
    const themeSettings = window.theme && window.theme.settings;
    const themeStrings = window.theme && window.theme.strings && window.theme.strings.cart && window.theme.strings.cart.general;

    if (remainingQuantity <= 0) {
      // First try to get the message from theme settings
      if (themeSettings && themeSettings.max_quantity_reached_message) {
        message = themeSettings.max_quantity_reached_message;
      } else if (themeStrings && themeStrings.max_quantity_reached) {
        message = themeStrings.max_quantity_reached;
      } else {
        message = `You already have the maximum allowed quantity (${maxQuantity}) of this product in your cart.`;
      }
      // Replace the placeholder with the actual max quantity
      message = message.replace('{{ max }}', maxQuantity);
    } else {
      // First try to get the message from theme settings
      if (themeSettings && themeSettings.max_quantity_remaining_message) {
        message = themeSettings.max_quantity_remaining_message;
      } else if (themeStrings && themeStrings.max_quantity_remaining) {
        message = themeStrings.max_quantity_remaining;
      } else {
        message = `You can only add ${remainingQuantity} more of this product. Maximum allowed is ${maxQuantity}.`;
      }
      // Replace the placeholders with the actual values
      message = message.replace('{{ remaining }}', remainingQuantity).replace('{{ max }}', maxQuantity);
    }

    // Check if this is a product card form
    const hasProductCardParent = form.closest('product-card') !== null;
    const hasCardInnerParent = form.closest('.card__inner') !== null;
    const isProductCard = hasProductCardParent || hasCardInnerParent;
    const isQuickAddForm = form.classList.contains('quick-add__form');
    const isInProductCard = form.closest('.product-card') !== null;
    const isProductCardForm = form.classList.contains('product-card-form');

    // For product cards, use the theme's notification system if available
    if (isProductCard || isQuickAddForm || isInProductCard || isProductCardForm) {
      // Try to use the theme's notification system if available
      if (window.theme && window.theme.showNotification) {
        window.theme.showNotification({
          message: message,
          type: 'error',
          duration: 5000
        });
        return;
      }

      // If the theme doesn't have a notification system, use our custom one
      this.showProductCardError(message);
      return;
    }

    // For other forms, try to find the error message container
    const errorContainer = form.querySelector('.product-form__error-message-wrapper');
    if (errorContainer) {
      // Find or create the error message element
      let errorMessage = errorContainer.querySelector('.product-form__error-message');
      if (!errorMessage) {
        errorMessage = document.createElement('div');
        errorMessage.classList.add('product-form__error-message');
        errorContainer.appendChild(errorMessage);
      }

      // Set the error message text
      errorMessage.textContent = message;

      // The styling is now handled by CSS in theme.css

      // Make sure the error container is visible
      errorContainer.style.display = 'block';
      errorContainer.hidden = false;

      // Ensure the error message is visible
      errorMessage.style.display = 'block';

      // Scroll to the error message if it's not in view
      if (!this.isElementInViewport(errorContainer)) {
        errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Hide the message after 5 seconds
      setTimeout(() => {
        errorContainer.hidden = true;
      }, 5000);

      return;
    }
    // If no error container exists, use the notification popup
    this.showNotificationPopup(message);
  }

  showProductCardError(message) {
    this.showCornerNotification(message);
  }

  showNotificationPopup(message) {
    console.log('Showing notification popup with message:', message);

    // First, remove any existing notification banners
    const existingBanners = document.querySelectorAll('.max-quantity-notification-banner');
    existingBanners.forEach(banner => {
      if (banner.parentNode) {
        banner.parentNode.removeChild(banner);
      }
    });

    // Create a notification banner
    const banner = document.createElement('div');
    banner.className = 'notification-banner notification-banner--error max-quantity-notification-banner';
    banner.setAttribute('role', 'alert');

    // Apply explicit styling to ensure visibility
    banner.style.position = 'fixed';
    banner.style.top = '20px';
    banner.style.left = '50%';
    banner.style.transform = 'translateX(-50%)';
    banner.style.zIndex = '999999';
    banner.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
    banner.style.maxWidth = '90%';
    banner.style.width = 'auto';
    banner.style.backgroundColor = '#e32c2b';
    banner.style.color = '#ffffff';
    banner.style.padding = '12px 20px';
    banner.style.borderRadius = '4px';
    banner.style.fontWeight = 'bold';
    banner.style.textAlign = 'center';
    banner.style.pointerEvents = 'auto';
    banner.style.opacity = '1';

    // Add a close button
    banner.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <p style="margin: 0; padding: 0; flex: 1;">${message}</p>
        <button style="background: none; border: none; color: white; font-size: 20px; cursor: pointer; margin-left: 10px;" onclick="this.parentNode.parentNode.remove();">×</button>
      </div>
    `;

    console.log('Banner created:', banner);

    // Add the banner to the page
    document.body.appendChild(banner);
    console.log('Banner added to document body');

    // Remove the banner after 5 seconds
    setTimeout(() => {
      console.log('Starting banner removal animation');
      banner.style.opacity = '0';
      banner.style.transition = 'opacity 0.3s ease';
      setTimeout(() => {
        if (banner.parentNode) {
          document.body.removeChild(banner);
          console.log('Banner removed from document');
        }
      }, 300);
    }, 5000);
  }

  showCornerNotification(message) {
    // First, remove any existing corner notifications
    const existingNotifications = document.querySelectorAll('.max-quantity-corner-notification');
    existingNotifications.forEach(notification => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    });

    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'max-quantity-corner-notification';
    notification.setAttribute('role', 'alert');

    // Add content without button
    notification.innerHTML = `
      <div>
        <p style="margin: 0; padding: 0;">${message}</p>
      </div>
    `;

    // Add to document
    document.body.appendChild(notification);

    // Force a reflow before adding the show class
    notification.offsetHeight;

    // Show the notification with animation
    setTimeout(() => {
      notification.classList.add('show');
    }, 10);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      notification.classList.remove('show');

      // Remove from DOM after animation completes
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 5000);
  }

  isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }
}

// Initialize the validator
document.addEventListener('DOMContentLoaded', () => {
  new MaxQuantityValidator();
});
